import { useQuery } from "@apollo/client";
import {
  Box,
  Card,
  CardContent,
  Link as MuiLink,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { TASKS_COUNT_QUERY } from "components/Tasks/schema";
import { TasksDisplay } from "components/Tasks/TasksDisplay";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { Link, useParams } from "react-router-dom";

function TaskStatCard({
  icon,
  label,
  count,
  loading,
}: {
  icon: string;
  label: string;
  count: number;
  loading: boolean;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        width: 200,
        height: 52,
        display: "flex",
        alignItems: "center",
        backgroundColor: grey[50],
        borderRadius: 0,
      }}
    >
      <CardContent sx={{ pt: "4px", pb: "4px", px: "16px", "&:last-child": { pb: "4px" } }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1, display: "block", mb: "2px" }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={30} />
        ) : (
          <Typography variant="body2" fontWeight="bold">
            {icon} {count}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export const TasksSection: React.FC = () => {
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();

  const baseSearch = `tenantId:${tenantId} facilityId:${facilityId}`;
  const fourteenDaysFromNow = DateTime.local().plus({ days: 14 }).toISODate();

  const { data: overdueData, loading: overdueLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:OVERDUE` },
    fetchPolicy: "cache-and-network",
  });

  const { data: dueSoonData, loading: dueSoonLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:ACTIVE before:${fourteenDaysFromNow}` },
    fetchPolicy: "cache-and-network",
  });

  const additionalFilterColumns: FilterKey[] = [
    { key: "after", header: "Due After", filterKeyType: "date" },
    { key: "before", header: "Due Before", filterKeyType: "date" },
  ];

  const commandButtons = () => [] as React.ReactNode[];

  const defaultSearch = `tenantId:${tenantId} facilityId:${facilityId}`;

  return (
    <>
      <Typography variant="h5" mb={1}>
        Facility Tasks
      </Typography>
      <Typography variant="body2" color="text.primary" mb={3}>
        Showing all current facility tasks. To see all tasks or add a new one, open{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/tasks`} underline="none">
          Tasks
        </MuiLink>
        .
      </Typography>
      <Stack direction="row" spacing="16px" sx={{ mb: "24px" }}>
        <TaskStatCard
          icon="🖍️"
          label="Overdue"
          count={overdueData?.virtualEvents.count ?? 0}
          loading={overdueLoading}
        />
        <TaskStatCard
          icon="💥"
          label="Due within 14 days"
          count={dueSoonData?.virtualEvents.count ?? 0}
          loading={dueSoonLoading}
        />
      </Stack>
      <Box sx={{ mx: -3 }}>
        <TasksDisplay
          additionalFilterColumns={additionalFilterColumns}
          commandButtons={commandButtons}
          defaultSearch={defaultSearch}
        />
      </Box>
    </>
  );
};
